/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : uiSlice.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for UI component state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 15, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 15, 2020
 * Description   : Add code to maintain state for expand all buttons and notification
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'stores/GlobalStore';
import { ACCORDION_SECTIONS } from 'components/ConfigureAccordion';
import { IUIState, IErrorState } from 'models/ui';
import { stopSaveConfig } from 'stores/ConfigureWorkFlowStore';

const initialState: IUIState = {
  configure: {
    expandAircraftDetails: true,
    expandSoftwareFeatures: false,
    currentStep: 1,
  },
  errorNotification: {
    error: null,
  },
  showAgentModal: false,
  AllAgentCustFlag: 'All'
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleConfigureAccordions(
      uiState: IUIState,
      action: PayloadAction<{ accordion: ACCORDION_SECTIONS; expand: boolean }>
    ) {
      const { accordion, expand } = action.payload;

      if (accordion === ACCORDION_SECTIONS.AircraftDetails) {
        uiState.configure.expandAircraftDetails = expand;
      } else if (accordion === ACCORDION_SECTIONS.SoftwareFeatures) {
        uiState.configure.expandSoftwareFeatures = expand;
      } else if (accordion === ACCORDION_SECTIONS.Both) {
        uiState.configure.expandAircraftDetails = expand;
        uiState.configure.expandSoftwareFeatures = expand;
      }
    },
    setConfigureCurrentStep(uiState: IUIState, action: PayloadAction<number>) {
      uiState.configure.currentStep = action.payload;

      if (action.payload === 3) {
        uiState.configure.expandAircraftDetails = initialState.configure.expandAircraftDetails;
        uiState.configure.expandSoftwareFeatures = initialState.configure.expandSoftwareFeatures;
        uiState.errorNotification = initialState.errorNotification;
      } else if (action.payload === 4) {
        uiState.errorNotification = initialState.errorNotification;
      }
    },
    setErrorNotification(uiState: IUIState, action: PayloadAction<IErrorState | null>) {
      uiState.errorNotification.error = action.payload;
    },
    setShowAgentModal(uiState: IUIState, action: PayloadAction<boolean>) {
      uiState.showAgentModal = action.payload;
    },
    setAllAgentCustFlag(uiState: IUIState, action: PayloadAction<'All'>) {
      uiState.AllAgentCustFlag = action.payload;
    },
  },
  extraReducers: builder =>
    builder
      .addCase('customer/setSelectedCustomer', (uiState: IUIState) => {
        uiState.configure.currentStep = 1;
      })
      .addCase(stopSaveConfig, (uiState: IUIState, action) => {
        if (action.payload.errorMessage === 'Python validation failed') {
          uiState.configure.currentStep = 4;
        }
      })
      .addCase('configureWorkFlow/setConfigAfterSaveDetails', (uiState: IUIState) => {
        uiState.configure.currentStep = 4;
      })
      .addCase('configureWorkFlow/setSaveFinalConfig', (uiState: IUIState) => {
        uiState.configure.currentStep = 5;
      }),
});

export const { toggleConfigureAccordions, setConfigureCurrentStep, setShowAgentModal, setErrorNotification } = uiSlice.actions;

export const uiReducer = uiSlice.reducer;

export const selectConfigureState = (state: RootState) => state.ui.configure;
export const selectShowAgentModal = (state: RootState) => state.ui.showAgentModal;
export const selectConfigureCurrentStep = (state: RootState) => state.ui.configure.currentStep;
export const selectErrorNotification = (state: RootState) => state.ui.errorNotification;
export const selectAllAgentCustFlag = (state: RootState) => state.ui.AllAgentCustFlag;
